import { useEffect, useState } from 'react';

import API from 'js/lib/api';

const enterpriseProvisionConfigApi = API('/api/enterpriseProvisionConfigurations.v1');

type ByJitEnabledResponse = {
  isJitEnabled: boolean;
};

type UseIsJitOrganizationResult = {
  isJit: boolean;
  isLoading: boolean;
};

type UseIsJitOrganizationParams = {
  orgId: string | undefined;
  skip?: boolean;
};

/**
 *
 * Custom hook to check if a JIT organization is enabled
 * @param orgId - The ID of the organization to check if JIT is enabled
 * @param skip - Whether to skip the check
 *
 * @returns An object containing the JIT status and loading state
 */
export const useIsJitOrganization = ({
  orgId,
  skip = false,
}: UseIsJitOrganizationParams): UseIsJitOrganizationResult => {
  const [isJit, setIsJit] = useState(false);
  const [isLoading, setIsLoading] = useState(!skip && !!orgId);

  useEffect(() => {
    if (skip || !orgId) {
      setIsJit(false);
      setIsLoading(false);
      return () => {};
    }

    setIsLoading(true);
    let cancelled = false;

    const queryString = new URLSearchParams({
      action: 'isJitEnabled',
      thirdPartyOrganizationId: orgId,
    }).toString();

    enterpriseProvisionConfigApi
      .post<ByJitEnabledResponse>(`?${queryString}`)
      .then((response) => {
        if (!cancelled) {
          setIsJit(response?.isJitEnabled === true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsJit(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, skip]);

  return { isJit, isLoading };
};
